import {
  getOrganizationTreeSetupLabel,
  isClubServiceVariant,
  getServiceInstanceScopeLabel,
  isAcademicServiceVariant,
  isCommunityAdminRole,
  isSocialServiceVariant,
  isEventServiceVariant,
  isFundraisingServiceVariant,
  isMembershipServiceVariant,
  isOrganizationServiceVariant,
  isServicesProductVariant,
  isTempleServiceVariant,
} from '@/lib/service-variant-types'

export { getOrganizationTreeSetupLabel, getServiceInstanceScopeLabel } from '@/lib/service-variant-types'

export const SERVICE_INSTANCE_PARAM = 'service_instance_id'
export const NODE_ID_PARAM = 'node_id'

/** Administration workspace for root org and tenant roles (replaces legacy `/org/tree`). */
export const ORG_ORGANIZATION_PAGE_PATH = '/org/organization'

/** Branch-operator workspace (org-branch variant); application-focused landing. */
export const ORG_BRANCH_WORKSPACE_PATH = '/org/branch'

/** Shared dashboard shell. The page renders the dashboard/home experience for the active variant. */
export const SERVICE_VARIANT_DASHBOARD_PATH = '/dashboard'

/** Service-variant entry point for non-org variants before resolving to their own dashboard/homepage. */
export const SERVICE_VARIANT_HOME_REDIRECT_PATH = '/services/dashboard'

/** Services product variant (1400) entry point — job list. */
export const SERVICES_VARIANT_HOME_PATH = '/job'

/** Membership in-app variant entry point before resolving to the membership workspace. */
export const MEMBERSHIP_VARIANT_HOME_REDIRECT_PATH = '/membership/dashboard'

/** Academic in-app variant entry point. */
export const ACADEMIC_VARIANT_HOME_PATH = '/academic/dashboard'

/** Club in-app variant entry point. */
export const CLUB_VARIANT_HOME_PATH = '/club/dashboard'

/** Community Admin dashboard for the Sangam social workspace. */
export const COMMUNITY_VARIANT_HOME_PATH = '/community/dashboard'

const BRANCH_VARIANT_KEYS = new Set(['101', '103', 'branch', 'org-branch', 'org_branch'])

/** Canonical path for org service instance settings form (`:id` is the tree node id). */
export const ORG_SERVICE_INSTANCE_FORM_BASE = '/org/service-instance/form'

/** Canonical base for listing instances under a tree node (`…/:id`). */
export const ORG_SERVICE_INSTANCE_LIST_BASE = '/org/service-instance'

/** Public privacy policy page (`?si=` service instance id). */
export const ORG_PUBLIC_PRIVACY_POLICY_PATH = '/org/privacy-policy'

export function buildPublicPrivacyPolicyPath(serviceInstanceId: string | number | null | undefined): string {
  const normalized = String(serviceInstanceId ?? '').trim()
  if (!normalized) {
    return ORG_PUBLIC_PRIVACY_POLICY_PATH
  }
  return `${ORG_PUBLIC_PRIVACY_POLICY_PATH}?si=${encodeURIComponent(normalized)}`
}

/** Public account deletion page (`?si=` service instance id). */
export const ORG_PUBLIC_ACCOUNT_DELETION_PATH = '/org/account-deletion'

export function buildPublicAccountDeletionPath(serviceInstanceId: string | number | null | undefined): string {
  const normalized = String(serviceInstanceId ?? '').trim()
  if (!normalized) {
    return ORG_PUBLIC_ACCOUNT_DELETION_PATH
  }
  return `${ORG_PUBLIC_ACCOUNT_DELETION_PATH}?si=${encodeURIComponent(normalized)}`
}

export function buildOrgServiceInstanceFormPath(
  nodeId: string | number,
  query?: string | URLSearchParams,
): string {
  const base = `${ORG_SERVICE_INSTANCE_FORM_BASE}/${nodeId}`
  if (query == null) {
    return base
  }

  const search = typeof query === 'string'
    ? (query.startsWith('?') ? query.slice(1) : query)
    : query.toString()
  return search ? `${base}?${search}` : base
}

/** List of service instances under an org tree node (`…/:nodeId`). */
export function buildOrgServiceInstanceListPath(nodeId: string | number): string {
  return `${ORG_SERVICE_INSTANCE_LIST_BASE}/${nodeId}`
}

export function getServiceVariantHomePath(post?: PostServiceInstanceContext | null): string {
  if (!post || isOrganizationScopePost(post)) {
    return SERVICE_VARIANT_DASHBOARD_PATH
  }

  if (isTempleServiceVariant(post.service_variant_unique_id, post.service_variant_name ?? post.service_variant_type)) {
    return '/venue/dashboard'
  }

  if (isAcademicServiceVariant(post.service_variant_unique_id, post.service_variant_name ?? post.service_variant_type)) {
    return ACADEMIC_VARIANT_HOME_PATH
  }

  if (isClubServiceVariant(post.service_variant_unique_id, post.service_variant_name ?? post.service_variant_type)) {
    return CLUB_VARIANT_HOME_PATH
  }

  if (
    isCommunityAdminRole(post)
    || isSocialServiceVariant(post.service_variant_unique_id, post.service_variant_name ?? post.service_variant_type)
  ) {
    return COMMUNITY_VARIANT_HOME_PATH
  }

  const hierarchyTierCode = String(post.level_variant_vertical_system_code ?? '').trim().toUpperCase()
  if (hierarchyTierCode === 'BRANCH') {
    return ORG_BRANCH_WORKSPACE_PATH
  }

  if (isServicesProductVariant(post.service_variant_unique_id, post.service_variant_name ?? post.service_variant_type)) {
    return SERVICES_VARIANT_HOME_PATH
  }

  return SERVICE_VARIANT_HOME_REDIRECT_PATH
}

export function buildServiceVariantHomePath(
  post?: PostServiceInstanceContext | null,
  serviceInstanceId?: string | null,
): string {
  return withServiceInstanceId(getServiceVariantHomePath(post), serviceInstanceId)
}

/** Fundraising in-app variant entry point. */
export const FUNDRAISING_VARIANT_HOME_PATH = '/fundraising/dashboard'

/** Venue in-app variant entry point (Temple). */
export const VENUE_VARIANT_HOME_PATH = '/venue/dashboard'

/** Events in-app variant entry point. */
export const EVENT_VARIANT_HOME_PATH = '/events/dashboard'

export function getServiceVariantHomePathFromVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): string {
  const normalizedVariantId = String(variantUniqueId ?? '').trim().toLowerCase()
  const normalizedVariantLabel = String(variantLabel ?? '').trim().toLowerCase()

  if (isOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return SERVICE_VARIANT_DASHBOARD_PATH
  }

  if (BRANCH_VARIANT_KEYS.has(normalizedVariantId) || BRANCH_VARIANT_KEYS.has(normalizedVariantLabel)) {
    return ORG_BRANCH_WORKSPACE_PATH
  }

  if (isMembershipServiceVariant(variantUniqueId, variantLabel)) {
    return MEMBERSHIP_VARIANT_HOME_REDIRECT_PATH
  }

  if (isAcademicServiceVariant(variantUniqueId, variantLabel)) {
    return ACADEMIC_VARIANT_HOME_PATH
  }

  if (isClubServiceVariant(variantUniqueId, variantLabel)) {
    return CLUB_VARIANT_HOME_PATH
  }

  if (isSocialServiceVariant(variantUniqueId, variantLabel)) {
    return COMMUNITY_VARIANT_HOME_PATH
  }

  if (isFundraisingServiceVariant(variantUniqueId, variantLabel)) {
    return FUNDRAISING_VARIANT_HOME_PATH
  }

  if (isTempleServiceVariant(variantUniqueId, variantLabel)) {
    return VENUE_VARIANT_HOME_PATH
  }

  if (isEventServiceVariant(variantUniqueId, variantLabel)) {
    return EVENT_VARIANT_HOME_PATH
  }

  if (isServicesProductVariant(variantUniqueId, variantLabel)) {
    return SERVICES_VARIANT_HOME_PATH
  }

  return SERVICE_VARIANT_HOME_REDIRECT_PATH
}

export function buildServiceVariantHomePathFromVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  serviceInstanceId?: string | null,
): string {
  return withServiceInstanceId(
    getServiceVariantHomePathFromVariant(variantUniqueId, variantLabel),
    serviceInstanceId,
  )
}

type ServiceInstanceWorkspaceSource = {
  id?: string | number | null
  instance_id?: string | null
  name?: string | null
  title?: string | null
  service_variant?: string | number | null
  service_variant_label?: string | null
  service_variant_name?: string | null
  level_variant_name?: string | null
  level_variant_unique_id?: string | null
  logo?: string | null
  service_instance_logo?: string | null
  org_logo?: string | null
  service_data?: {
    variant_unique?: string | number | null
    service_name?: string | null
  } | null
}

export function mergePostWithServiceInstance<T extends PostServiceInstanceContext>(
  post: T | null | undefined,
  instance: ServiceInstanceWorkspaceSource | null | undefined,
): T | null {
  if (!post) {
    return null
  }

  if (!instance) {
    return post
  }

  const instanceId = normalizeServiceInstanceCandidate(instance.instance_id)
    || normalizeServiceInstanceCandidate(instance.id)
  const variantUniqueId = instance.service_variant ?? instance.service_data?.variant_unique
  const variantLabel = instance.service_variant_label
    ?? instance.service_variant_name
  const instanceLogo = instance.service_instance_logo ?? instance.logo ?? instance.org_logo

  return {
    ...post,
    ...(instanceId ? {
      service_instance: instanceId,
      service_instance_id: instanceId,
    } : {}),
    ...(instance.name || instance.title ? {
      service_instance_name: String(instance.name ?? instance.title).trim(),
    } : {}),
    ...(variantUniqueId != null && String(variantUniqueId).trim() ? {
      service_variant_unique_id: variantUniqueId,
    } : {}),
    ...(variantLabel ? {
      service_variant_name: variantLabel,
      service_variant_type: variantLabel,
    } : {}),
    ...(instance.level_variant_name ? { level_variant_name: instance.level_variant_name } : {}),
    ...(instance.level_variant_unique_id ? { level_variant_unique_id: instance.level_variant_unique_id } : {}),
    ...(instanceLogo ? { service_instance_logo: instanceLogo } : {}),
  }
}

export function resolveWorkspacePostContext<T extends PostServiceInstanceContext>({
  post,
  availablePosts,
  serviceInstanceId,
  nodeInstances,
}: {
  post?: T | null
  availablePosts?: T[] | null
  serviceInstanceId?: string | null
  nodeInstances?: ServiceInstanceWorkspaceSource[] | null
}): T | null {
  if (!post) {
    return null
  }

  const scopedPost = resolveScopedPostForServiceInstance({
    currentPost: post,
    availablePosts,
    serviceInstanceId,
    includeOrganizationScopeFallback: true,
  }) ?? post

  const normalizedServiceInstanceId = normalizeServiceInstanceCandidate(serviceInstanceId)
  if (!normalizedServiceInstanceId || !nodeInstances?.length) {
    return scopedPost
  }

  const matchingInstance = nodeInstances.find((instance) => {
    const instanceId = normalizeServiceInstanceCandidate(instance.instance_id)
      || normalizeServiceInstanceCandidate(instance.id)
    return instanceId === normalizedServiceInstanceId
  })

  if (!matchingInstance) {
    return scopedPost
  }

  return mergePostWithServiceInstance(scopedPost, matchingInstance)
}

export function getNodeLinkedServiceInstanceIds(
  nodeInstances?: ServiceInstanceWorkspaceSource[] | null,
): string[] {
  if (!nodeInstances?.length) {
    return []
  }

  const ids = new Set<string>()
  for (const instance of nodeInstances) {
    const instanceId = normalizeServiceInstanceCandidate(instance.instance_id)
      || normalizeServiceInstanceCandidate(instance.id)
    if (instanceId) {
      ids.add(instanceId)
    }
  }

  return Array.from(ids)
}

/**
 * When a role node owns multiple service instances, explicit workspace selection
 * (URL param or stored choice) must win over the post's default service_instance.
 */
export function resolveWorkspaceActiveServiceInstanceId({
  search,
  post,
  currentBranch,
  availableBranches,
  storedServiceInstanceId,
  token,
  nodeInstanceIds,
}: {
  search?: string
  post?: PostServiceInstanceContext | null
  currentBranch?: BranchServiceInstanceContext | null
  availableBranches?: BranchServiceInstanceContext[] | null
  storedServiceInstanceId?: string | null
  token?: string | null
  nodeInstanceIds?: string[] | null
}): string | null {
  const baseResolved = resolveActiveServiceInstanceId({
    search,
    post,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
    token,
  })

  const explicitUrlId = search ? getServiceInstanceIdFromSearch(search) : null
  if (explicitUrlId) {
    return explicitUrlId
  }

  const uniqueNodeInstanceIds = Array.from(new Set(
    (nodeInstanceIds ?? []).map((value) => String(value).trim()).filter(Boolean),
  ))

  if (uniqueNodeInstanceIds.length <= 1) {
    const postServiceInstanceId = getServiceInstanceIdFromPost(post)
    return postServiceInstanceId || baseResolved
  }

  const nodeIdSet = new Set(uniqueNodeInstanceIds)
  const belongsToNode = (value?: string | null) => Boolean(value && nodeIdSet.has(value))

  const normalizedStoredId = normalizeServiceInstanceCandidate(storedServiceInstanceId)
  if (belongsToNode(normalizedStoredId)) {
    return normalizedStoredId
  }

  const postServiceInstanceId = getServiceInstanceIdFromPost(post)
  if (belongsToNode(postServiceInstanceId)) {
    return postServiceInstanceId
  }

  if (belongsToNode(baseResolved)) {
    return baseResolved
  }

  return uniqueNodeInstanceIds[0] ?? baseResolved
}

function isFundraisingPost(post?: PostServiceInstanceContext | null): boolean {
  if (!post) {
    return false
  }
  return isFundraisingServiceVariant(
    post.service_variant_unique_id,
    post.service_variant_name ?? post.service_variant_type,
  )
}

function isEventPost(post?: PostServiceInstanceContext | null): boolean {
  if (!post) {
    return false
  }
  return isEventServiceVariant(
    post.service_variant_unique_id,
    post.service_variant_name ?? post.service_variant_type,
  )
}

/** Events in-app instance ids are typically prefixed with variant unique id `300`. */
export function isLikelyEventServiceInstanceId(value: string | null | undefined): boolean {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return false
  }
  return normalized === '300' || normalized.startsWith('300-')
}

/**
 * Events are owned by the Events in-app service instance. Prefer that SI over
 * a branch/tenant/fundraising SI accidentally left in the URL or auth store.
 */
export function resolveEventScopedServiceInstanceId({
  search,
  post,
  availablePosts,
  storedServiceInstanceId,
}: {
  search?: string
  post?: PostServiceInstanceContext | null
  availablePosts?: PostServiceInstanceContext[] | null
  storedServiceInstanceId?: string | null
}): string | null {
  const eventPosts = [post, ...(availablePosts ?? [])]
    .filter((entry): entry is PostServiceInstanceContext => Boolean(entry))
    .filter(isEventPost)

  const eventIds = new Set(
    eventPosts
      .map((entry) => getServiceInstanceIdFromPost(entry))
      .filter((value): value is string => Boolean(value)),
  )

  const fromUrl = search ? getServiceInstanceIdFromSearch(search) : null
  // Keep deep-linked Events SIs even when the signed-in Events post is a sibling SI.
  // Replacing them on refresh was causing /events/:id to 404 after reload.
  if (
    fromUrl
    && (eventIds.size === 0 || eventIds.has(fromUrl) || isLikelyEventServiceInstanceId(fromUrl))
  ) {
    return fromUrl
  }

  const fromPost = isEventPost(post) ? getServiceInstanceIdFromPost(post) : null
  if (fromPost) {
    return fromPost
  }

  const fromStored = normalizeServiceInstanceCandidate(storedServiceInstanceId)
  if (fromStored && eventIds.has(fromStored)) {
    return fromStored
  }

  for (const entry of eventPosts) {
    const candidate = getServiceInstanceIdFromPost(entry)
    if (candidate) {
      return candidate
    }
  }

  if (fromUrl && eventIds.size > 0 && !eventIds.has(fromUrl)) {
    return Array.from(eventIds)[0] ?? null
  }

  return fromUrl || fromStored || null
}

/**
 * Fundraising campaigns are owned by the fundraising in-app service instance.
 * Prefer that SI over a branch SI accidentally left in the URL/query.
 */
export function resolveFundraisingScopedServiceInstanceId({
  search,
  post,
  availablePosts,
  storedServiceInstanceId,
}: {
  search?: string
  post?: PostServiceInstanceContext | null
  availablePosts?: PostServiceInstanceContext[] | null
  storedServiceInstanceId?: string | null
}): string | null {
  const fundraisingPosts = [post, ...(availablePosts ?? [])]
    .filter((entry): entry is PostServiceInstanceContext => Boolean(entry))
    .filter(isFundraisingPost)

  const fundraisingIds = new Set(
    fundraisingPosts
      .map((entry) => getServiceInstanceIdFromPost(entry))
      .filter((value): value is string => Boolean(value)),
  )

  const fromUrl = search ? getServiceInstanceIdFromSearch(search) : null
  // Keep URL when it already points at a fundraising SI, or when we cannot
  // identify fundraising posts yet (avoid wiping a correct fundraising id).
  if (fromUrl && (fundraisingIds.size === 0 || fundraisingIds.has(fromUrl))) {
    return fromUrl
  }

  const fromPost = isFundraisingPost(post) ? getServiceInstanceIdFromPost(post) : null
  if (fromPost) {
    return fromPost
  }

  const fromStored = normalizeServiceInstanceCandidate(storedServiceInstanceId)
  if (fromStored && fundraisingIds.has(fromStored)) {
    return fromStored
  }

  for (const entry of fundraisingPosts) {
    const candidate = getServiceInstanceIdFromPost(entry)
    if (candidate) {
      return candidate
    }
  }

  // Do not fall back to a branch SI for fundraising API scope.
  if (fromUrl && fundraisingIds.size > 0 && !fundraisingIds.has(fromUrl)) {
    return Array.from(fundraisingIds)[0] ?? null
  }

  return fromUrl || fromStored || null
}

type PostServiceInstanceContext = {
  id?: string | number | null
  service_instance?: string | number | null
  service_instance_id?: string | number | null
  service_variant_unique_id?: string | number | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  role_name?: string | null
  level_variant_vertical_system_code?: string | null
  capabilities?: Record<string, unknown> | null
  roles_data?: {
    id?: string | number | null
    roles_name?: string | null
    unique_id?: string | null
    permissions?: Record<string, unknown> | null
    role_level?: number | null
    role_level_code?: string | null
    role_level_label?: string | null
  } | null
}

type BranchServiceInstanceContext = {
  id?: string | number | null
  instanceId?: string | null
  code?: string | null
}

function normalizeServiceInstanceCandidate(value?: string | number | null): string | null {
  if (value === undefined || value === null) {
    return null
  }

  const normalizedValue = String(value).trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

function isLikelyPublicServiceInstanceId(value: string) {
  return /[A-Za-z_-]/.test(value)
}

function isOrganizationScopePost(post?: PostServiceInstanceContext | null) {
  return isOrganizationServiceVariant(
    post?.service_variant_unique_id,
    post?.service_variant_name ?? post?.service_variant_type,
  )
}

function isExactServiceInstanceMatch(
  post: PostServiceInstanceContext | null | undefined,
  serviceInstanceId: string,
) {
  return getServiceInstanceIdFromPost(post) === serviceInstanceId
}

function getServiceInstanceIdFromBranch(branch?: BranchServiceInstanceContext | null): string | null {
  return normalizeServiceInstanceCandidate(branch?.instanceId)
    || normalizeServiceInstanceCandidate(branch?.code)
    || normalizeServiceInstanceCandidate(branch?.id)
}

function isBranchMatch(branch: BranchServiceInstanceContext | null | undefined, serviceInstanceId: string) {
  const branchCandidates = [branch?.instanceId, branch?.code, branch?.id]
    .map((value) => normalizeServiceInstanceCandidate(value))
    .filter((value): value is string => Boolean(value))

  return branchCandidates.includes(serviceInstanceId)
}

function isKnownServiceInstanceIdInContext({
  serviceInstanceId,
  post,
  currentBranch,
  availableBranches,
  token,
}: {
  serviceInstanceId: string
  post?: PostServiceInstanceContext | null
  currentBranch?: BranchServiceInstanceContext | null
  availableBranches?: BranchServiceInstanceContext[] | null
  token?: string | null
}) {
  if (isExactServiceInstanceMatch(post, serviceInstanceId)) {
    return true
  }

  if (isBranchMatch(currentBranch, serviceInstanceId)) {
    return true
  }

  if ((availableBranches ?? []).some((branch) => isBranchMatch(branch, serviceInstanceId))) {
    return true
  }

  return getServiceInstanceIdFromToken(token) === serviceInstanceId
}

export function getPostRoleLevel(post?: PostServiceInstanceContext | null): number | null {
  const directLevel = post?.roles_data?.role_level
  if (typeof directLevel === 'number' && Number.isFinite(directLevel)) {
    return directLevel
  }

  const levelCode = String(post?.roles_data?.role_level_code ?? '').trim().toLowerCase()
  const codeMatch = levelCode.match(/role_level_(\d+)/)
  if (codeMatch) {
    const parsed = Number(codeMatch[1])
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  const levelLabel = String(post?.roles_data?.role_level_label ?? '').trim()
  const labelMatch = levelLabel.match(/^L(\d+)$/i)
  if (labelMatch) {
    const parsed = Number(labelMatch[1])
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return null
}

function getCapabilityCount(post?: PostServiceInstanceContext | null) {
  const capabilities = post?.capabilities
  if (!capabilities || typeof capabilities !== 'object') {
    return 0
  }

  return Object.values(capabilities).filter((value) => value === true).length
}

function getScopedPostPriority(post?: PostServiceInstanceContext | null) {
  const capabilityCount = getCapabilityCount(post)
  const roleLevel = getPostRoleLevel(post)
  const roleLevelBonus = roleLevel === null ? 0 : Math.max(0, 20 - roleLevel)

  return capabilityCount * 100 + roleLevelBonus
}

export function resolveScopedPostForServiceInstance<T extends PostServiceInstanceContext>({
  currentPost,
  availablePosts,
  serviceInstanceId,
  preferMostCapable = false,
  includeOrganizationScopeFallback = false,
}: {
  currentPost?: T | null
  availablePosts?: T[] | null
  serviceInstanceId?: string | null
  preferMostCapable?: boolean
  includeOrganizationScopeFallback?: boolean
}): T | null {
  const targetServiceInstanceId = normalizeServiceInstanceCandidate(serviceInstanceId)
  const posts = [currentPost, ...(availablePosts ?? [])].filter((post): post is T => Boolean(post))

  if (!targetServiceInstanceId) {
    return currentPost ?? posts[0] ?? null
  }

  const exactMatchingPosts = posts.filter((post) => isExactServiceInstanceMatch(post, targetServiceInstanceId))
  const organizationFallbackPosts = includeOrganizationScopeFallback
    ? posts.filter((post) => (
      !isExactServiceInstanceMatch(post, targetServiceInstanceId)
      && isOrganizationScopePost(post)
    ))
    : []
  const candidatePosts = exactMatchingPosts.length > 0
    ? [...exactMatchingPosts, ...organizationFallbackPosts]
    : organizationFallbackPosts

  if (candidatePosts.length === 0) {
    return currentPost ?? posts[0] ?? null
  }

  const currentMatchesTarget = currentPost && isExactServiceInstanceMatch(currentPost, targetServiceInstanceId)
  // Preserve the actively selected post whenever it already matches the
  // requested service instance. Do not silently escalate to a more capable
  // role (for example root admin), because that feels like an automatic role
  // switch in the portal.
  if (currentMatchesTarget && !preferMostCapable) {
    return currentPost
  }

  return candidatePosts
    .slice()
    .sort((left, right) => getScopedPostPriority(right) - getScopedPostPriority(left))[0] ?? null
}

function shouldUseCurrentBranchFallback(
  post: PostServiceInstanceContext | null | undefined,
  postServiceInstanceId: string | null,
  currentBranch: BranchServiceInstanceContext | null | undefined,
  availableBranches: BranchServiceInstanceContext[] | null | undefined,
) {
  if (!postServiceInstanceId || !currentBranch || isOrganizationScopePost(post)) {
    return false
  }

  const branches = availableBranches ?? []
  if (branches.length === 0) {
    return false
  }

  return !branches.some((branch) => isBranchMatch(branch, postServiceInstanceId))
}

/** Normalizes API values (e.g. "1", "2") to `local`, `s3`, or `not_configured`. */
export function normalizeServiceStorageType(
  value: string | null | undefined,
): 'local' | 's3' | 'not_configured' {
  const t = String(value ?? '')
    .toLowerCase()
    .trim()
  if (t === '2' || t === 's3') {
    return 's3'
  }
  if (t === '1' || t === 'local') {
    return 'local'
  }
  if (t === 'not_configured') {
    return 'not_configured'
  }
  return 'not_configured'
}

function resolveEffectiveServiceStorageType(
  instance: ServiceInstanceStorageShape,
): 'local' | 's3' | 'not_configured' {
  const ownMode = normalizeServiceStorageType(instance.storage_type)
  if (ownMode !== 'not_configured') {
    return ownMode
  }
  return normalizeServiceStorageType(instance.resolved_storage_type)
}

function resolveEffectiveServiceStoragePath(
  instance: ServiceInstanceStorageShape,
): string {
  const ownMode = normalizeServiceStorageType(instance.storage_type)
  if (ownMode !== 'not_configured') {
    return String(instance.static_file_path ?? '').trim()
  }
  return String(instance.resolved_static_file_path ?? instance.static_file_path ?? '').trim()
}

export type ServiceInstanceStorageShape = {
  storage_type?: string | null
  resolved_storage_type?: string | null
  static_file_path?: string | null
  resolved_static_file_path?: string | null
  storage_bucket_name?: string | null
  storage_access_key?: string | null
  storage_secret_key?: string | null
  storage_region_name?: string | null
  storage_ready?: boolean | null
}

/** Whether uploads can proceed given instance storage fields (aligned with backend FileUpload checks). */
export function getServiceInstanceStorageReadiness(
  instance: ServiceInstanceStorageShape | null | undefined,
): { ready: boolean; reason: string | null } {
  if (!instance) {
    return { ready: false, reason: 'Organization context could not be loaded.' }
  }

  const mode = resolveEffectiveServiceStorageType(instance)
  const path = resolveEffectiveServiceStoragePath(instance)
  if (mode === 'not_configured') {
    return { ready: false, reason: 'Configure storage on this branch or on a parent service instance.' }
  }
  if (mode === 'local') {
    if (!path) {
      return { ready: false, reason: 'Set a storage path under Configuration (Local storage).' }
    }
    if (!path.startsWith('/')) {
      return { ready: false, reason: 'Local storage path must be an absolute filesystem path.' }
    }
    return { ready: true, reason: null }
  }

  if (mode === 's3') {
    const hasCredentials = instance.storage_ready === true
      || Boolean(
        instance.storage_bucket_name
        && instance.storage_access_key
        && instance.storage_secret_key
        && instance.storage_region_name,
      )
    if (!path && hasCredentials) {
      return { ready: true, reason: null }
    }
    if (!path) {
      return { ready: false, reason: 'Set an HTTPS storage path under Configuration for S3 storage, or configure bucket credentials under Credentials.' }
    }
  }

  if (mode === 's3' && path) {
    try {
      const url = new URL(path)
      if (url.protocol !== 'https:') {
        return { ready: false, reason: 'S3 storage path must use HTTPS.' }
      }
    } catch {
      return { ready: false, reason: 'S3 storage path must be a valid HTTPS URL.' }
    }
  }

  // storage_ready is set by the backend when inherited credentials are fully configured
  // (access_key and secret_key are intentionally masked in the response for security)
  if (instance.storage_ready === true) {
    return { ready: true, reason: null }
  }
  if (instance.storage_ready === false) {
    return { ready: false, reason: 'Complete S3 bucket, region, access key, and secret under Credentials.' }
  }

  // Fallback: check visible fields if storage_ready is not set
  const bucket = String(instance.storage_bucket_name ?? '').trim()
  const access = String(instance.storage_access_key ?? '').trim()
  const secret = String(instance.storage_secret_key ?? '').trim()
  const region = String(instance.storage_region_name ?? '').trim()
  if (!bucket || !access || !secret || !region) {
    return { ready: false, reason: 'Complete S3 bucket, region, access key, and secret under Credentials.' }
  }
  return { ready: true, reason: null }
}

export function getServiceInstanceIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(SERVICE_INSTANCE_PARAM)
  return value && value.trim() ? value : null
}

export function getNodeIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(NODE_ID_PARAM)
  return value && value.trim() ? value : null
}

export function getServiceInstanceIdFromPost(post?: PostServiceInstanceContext | null): string | null {
  const directServiceInstanceId = normalizeServiceInstanceCandidate(post?.service_instance)
  if (directServiceInstanceId) {
    return directServiceInstanceId
  }

  const fallbackServiceInstanceId = normalizeServiceInstanceCandidate(post?.service_instance_id)
  if (fallbackServiceInstanceId && isLikelyPublicServiceInstanceId(fallbackServiceInstanceId)) {
    return fallbackServiceInstanceId
  }

  return null
}

export function resolveActiveServiceInstanceId({
  search,
  post,
  currentBranch,
  availableBranches,
  storedServiceInstanceId,
  token,
}: {
  search?: string
  post?: PostServiceInstanceContext | null
  currentBranch?: BranchServiceInstanceContext | null
  availableBranches?: BranchServiceInstanceContext[] | null
  storedServiceInstanceId?: string | null
  token?: string | null
}): string | null {
  const explicitServiceInstanceId = search ? getServiceInstanceIdFromSearch(search) : null
  const postServiceInstanceId = getServiceInstanceIdFromPost(post)
  const currentBranchServiceInstanceId = getServiceInstanceIdFromBranch(currentBranch)

  if (explicitServiceInstanceId && isKnownServiceInstanceIdInContext({
    serviceInstanceId: explicitServiceInstanceId,
    post,
    currentBranch,
    availableBranches,
    token,
  })) {
    return explicitServiceInstanceId
  }

  if (shouldUseCurrentBranchFallback(post, postServiceInstanceId, currentBranch, availableBranches)) {
    return currentBranchServiceInstanceId
      || postServiceInstanceId
      || storedServiceInstanceId
      || getServiceInstanceIdFromToken(token)
  }

  return postServiceInstanceId
    || currentBranchServiceInstanceId
    || storedServiceInstanceId
    || getServiceInstanceIdFromToken(token)
}

export function withServiceInstanceId(path: string, serviceInstanceId?: string | null): string {
  if (!serviceInstanceId) {
    return path
  }

  const [pathWithoutHash, hash = ''] = path.split('#')
  const [pathname, search = ''] = pathWithoutHash.split('?')
  const params = new URLSearchParams(search)
  params.set(SERVICE_INSTANCE_PARAM, serviceInstanceId)

  const query = params.toString()
  const nextPath = query ? `${pathname}?${query}` : pathname
  return hash ? `${nextPath}#${hash}` : nextPath
}

export function withNodeId(path: string, nodeId?: string | number | null): string {
  const normalizedNodeId = String(nodeId ?? '').trim()
  if (!normalizedNodeId) {
    return path
  }

  const [pathname, search = ''] = path.split('?')
  const params = new URLSearchParams(search)
  params.set(NODE_ID_PARAM, normalizedNodeId)

  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

/** Manage Roles / Role Assignments links scoped to a branch node.
 * Pass the current workspace service_instance_id (Tenant/Org), not the branch SI —
 * branch scope is carried by nodeId (+ branch level variant) so the global in-app
 * selector does not switch identity context.
 */
export function withRoleManagementContext(
  path: string,
  context: { serviceInstanceId?: string | null; nodeId?: string | number | null },
): string {
  return withNodeId(withServiceInstanceId(path, context.serviceInstanceId), context.nodeId)
}

/** Preserve workspace + branch-directory query params on links (list ↔ form ↔ media). */
export function withWorkspaceListContext(
  path: string,
  context: { serviceInstanceId?: string | null; uniqueLevelVariantId?: string | null },
): string {
  const merged = withServiceInstanceId(path, context.serviceInstanceId)
  const ulv = String(context.uniqueLevelVariantId ?? '').trim()
  if (!ulv) {
    return merged
  }

  const [pathname, search = ''] = merged.split('?')
  const params = new URLSearchParams(search)
  const genericLevelVariant = ulv.replace(/-lvl-variant$/i, '') || ulv
  params.set('lvl_variant', genericLevelVariant)
  params.set('unique_level_variant_id', ulv)
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

export function getServiceInstanceIdFromToken(token?: string | null): string | null {
  if (!token) {
    return null
  }

  try {
    const [, payload] = token.split('.')
    if (!payload) {
      return null
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=')
    const decodedPayload = JSON.parse(window.atob(paddedPayload)) as { instance_id?: string }

    return decodedPayload.instance_id || null
  } catch {
    return null
  }
}

type ServiceInstanceVariantSource = {
  service_variant?: string | number | null
  service_variant_name?: string | null
  service_data?: { variant_unique?: string | number | null } | null
  level_variant_name?: string | null
}

export function getServiceInstanceScopeLabelFromInstance(
  instance: ServiceInstanceVariantSource | null | undefined,
): string {
  return getServiceInstanceScopeLabel(
    instance?.service_data?.variant_unique ?? instance?.service_variant,
    instance?.service_variant_name,
    instance?.level_variant_name,
  )
}

export function getOrganizationTreeSetupLabelFromInstance(
  instance: ServiceInstanceVariantSource | null | undefined,
): string {
  return getOrganizationTreeSetupLabel(
    instance?.service_data?.variant_unique ?? instance?.service_variant,
    instance?.service_variant_name,
    instance?.level_variant_name,
  )
}
